/**
 * S17 — renders a view's `table` representation from the engine's `TableData`
 * (`computeTableData`). Pure over the data model: rows = the view's shown-selection,
 * columns = getter (editable) / calc (read-only). A container computes the data from
 * the workspace context and mounts this when a view carries a `TableClause`.
 *
 * Editable getter cells surface a <textarea>; committing an edit routes through the
 * model-channel write (`setFieldCellEdit`) — the container supplies `onEditCell`.
 * Read-only cells (calc / builtin / capability) render as plain text.
 *
 * COLUMN editing (S17): when the container supplies `columns` (a {@link ColumnOps}),
 * the header row becomes interactive — drag to reorder (dnd-kit), a per-column ⋯
 * menu (rename / remove / add), and click-to-sort — each written back to the view's
 * `table` clause via `setTableColumns`. Without it the header is display-only.
 *
 * ## User flow (what the table is FOR + where its controls live)
 * The table answers "what are these elements' field values, side by side?" — a
 * spreadsheet over the shown selection. The reader/editor works top-down:
 *   1. the HEADER row (sticky at top) names each column; it stays pinned while a
 *      TALL table scrolls, so a long row list never loses its column labels. An
 *      editable column reads with an accent tint (its cells are writable); a
 *      read-only column (calc / builtin / capability) reads plain.
 *   2. SORT — click a getter column's title to sort by it (▲); click again to
 *      clear. The title doubles as the drag handle (a >4px drag reorders instead).
 *   3. COLUMN ops live on each header's ⋯ menu (rename / add / remove) — kept off
 *      the always-visible surface so the header stays a clean label row, revealed
 *      on demand at the column it acts on.
 *   4. CELL edit — an editable cell IS a <textarea> in place (Enter commits,
 *      Shift+Enter newlines); committing on blur writes the model slot. It
 *      auto-grows so a LONG editable value wraps in full, not clipped on one
 *      line. A read-only cell is plain text; an ABSENT cell shows a muted "·" so
 *      "no value" reads as legibly empty, not blank.
 * A WIDE table (many columns) scrolls horizontally inside its own region rather
 * than blowing out the panel; long cell values (read-only OR editable) wrap
 * within a bounded column width.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { MoreVertical } from "lucide-react";
import type { TableGetterSpec } from "@archlang/engine";
import { IconButton, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { useTranslation } from "../i18n";
import { groundProps } from "./viewFocus.js";
import type { ColumnOps } from "./useTableColumnEditing.js";
import { TableColumnPicker } from "./TableColumnPicker.js";
import { useWindow } from "./virtualization/windowing.js";
import { SpacerRow } from "./virtualization/Spacers.js";

/**
 * The render contract — structurally satisfied by the engine's `TableData`
 * (`computeTableData`), so the container passes that straight in. Kept local so
 * the component doesn't couple to the engine barrel (a view component renders a
 * view-model; it doesn't own the engine's internal type).
 */
export interface TableViewModel {
  readonly columns: readonly { readonly title: string; readonly editable: boolean }[];
  readonly rows: readonly { readonly key: string; readonly cells: readonly (string | number | boolean | null)[] }[];
}

export interface ViewTableViewProps {
  readonly data: TableViewModel;
  /** Commit an edited getter cell (rowKey, columnIndex, newValue). Omit = display-only. */
  readonly onEditCell?: (rowKey: string, columnIndex: number, value: string) => void;
  /** Column-list ops (reorder / add / remove / rename / sort). Omit = static header. */
  readonly columns?: ColumnOps | null;
  /** The view's `focus` set (origin-qualified keys — the same space
   *  `TableRowData.key` lives in). Non-empty ⇒ every row outside it is GROUND
   *  and recedes. Focus never reorders or filters: the ground is still there to
   *  be read, which is exactly what separates it from `hide`. */
  readonly focus?: ReadonlySet<string>;
}

export function ViewTableView({ data, onEditCell, columns, focus }: ViewTableViewProps): ReactNode {
  // The scroll region is the virtualizer's viewport. Above WINDOW_THRESHOLD rows
  // only the visible slice mounts (rows auto-grow → DYNAMIC measurement); below
  // it every row renders exactly as before (auto layout, no measured widths).
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowCount = data.rows.length;
  const win = useWindow(scrollRef, rowCount, { estimateSize: 34, dynamic: true });
  const colWidths = useMeasuredColWidths(scrollRef, data, win.windowed);
  const colCount = data.columns.length;
  const totalWidth = colWidths ? colWidths.reduce((a, b) => a + b, 0) : undefined;
  // Read-only cells carry a right-click "Copy" (universal context menu).
  const cellMenu = useContextMenu();

  const table = (
    <table
      className="arch-view-table"
      aria-rowcount={win.windowed ? rowCount : undefined}
      // Windowed tables pin `table-layout: fixed` + measured column widths so the
      // visible slice can't re-width columns as you scroll (auto layout would
      // jitter). Content-derived widths keep the auto-grow textarea + wrapping.
      style={win.windowed ? { tableLayout: "fixed", width: totalWidth } : undefined}
    >
      {win.windowed && colWidths && (
        <colgroup>
          {colWidths.map((w, i) => (
            <col key={i} style={{ width: w }} />
          ))}
        </colgroup>
      )}
      <TableHeader data={data} columns={columns} />
      <tbody>
        <SpacerRow height={win.before} cols={colCount} />
        {win.indices.map((ri) => {
          const row = data.rows[ri];
          if (!row) return null;
          return (
            <tr
              key={row.key}
              data-row-key={row.key}
              data-index={ri}
              aria-rowindex={win.windowed ? ri + 1 : undefined}
              ref={win.measureElement}
              {...groundProps(focus, row.key)}
            >
              {row.cells.map((cell, ci) => {
                const col = data.columns[ci];
                const text = cell == null ? "" : String(cell);
                if (col?.editable && onEditCell) {
                  return (
                    <EditableCell
                      key={ci}
                      rowKey={row.key}
                      columnIndex={ci}
                      columnTitle={col.title}
                      initial={text}
                      onEditCell={onEditCell}
                    />
                  );
                }
                return (
                  <td
                    key={ci}
                    data-editable={col?.editable ?? false}
                    data-absent={cell == null || undefined}
                    onContextMenu={
                      text
                        ? (e) =>
                            cellMenu.open(e, [
                              { label: "Copy", onClick: () => void navigator.clipboard?.writeText(text) },
                            ])
                        : undefined
                    }
                  >
                    {/* A block wrapper (not bare text) so a LONG value wraps within
                        a bounded width — `max-width` on a <td> is ignored under
                        table-layout:auto, but is honored on a block child. `title`
                        keeps the full value reachable on hover. */}
                    {text && (
                      <span className="arch-table-cell" title={text}>
                        {text}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
        <SpacerRow height={win.after} cols={colCount} />
      </tbody>
    </table>
  );
  // The DndContext wraps the WHOLE table (not the <tr>): dnd-kit renders a hidden
  // a11y <div> inline, and a <div> inside <tr>/<thead> is invalid HTML. The scroll
  // region (own overflow) contains a WIDE/TALL table instead of letting it blow out
  // the panel, and pairs with the sticky header (CSS) so a long row list keeps its
  // column labels.
  const body = columns
    ? <SortableTableFrame columns={columns} count={data.columns.length}>{table}</SortableTableFrame>
    : table;
  return (
    <div className="arch-view-table-wrap">
      <div className="arch-table-scroll" ref={scrollRef}>
        {body}
      </div>
      {cellMenu.node}
    </div>
  );
}

/**
 * An editable getter cell. Uncontrolled `<textarea>` (preserves auto-grow +
 * exact typing UX), but its live value is mirrored into a ref so the edit can
 * COMMIT ON UNMOUNT — a windowed row scrolled out of view unmounts, and React
 * does NOT fire `blur` on unmount, so a blur-only commit would silently drop the
 * edit. `committed` guards against a double-write (blur then unmount).
 */
function EditableCell({
  rowKey,
  columnIndex,
  columnTitle,
  initial,
  onEditCell,
}: {
  readonly rowKey: string;
  readonly columnIndex: number;
  readonly columnTitle: string;
  readonly initial: string;
  readonly onEditCell: (rowKey: string, columnIndex: number, value: string) => void;
}): ReactNode {
  const valRef = useRef(initial);
  const committedRef = useRef(initial);
  const cbRef = useRef(onEditCell);
  cbRef.current = onEditCell;
  const commit = (): void => {
    if (valRef.current !== committedRef.current) {
      cbRef.current(rowKey, columnIndex, valRef.current);
      committedRef.current = valRef.current;
    }
  };
  // Commit-on-unmount reads the mirrored ref (not the DOM, which may already be
  // detached when a passive-effect cleanup runs). Deps are per-cell stable, so
  // the cleanup fires only on real unmount.
  useEffect(() => () => commit(), [rowKey, columnIndex]);
  return (
    <td data-editable="true">
      {/* A textarea (not <input>) so a LONG editable value wraps + auto-grows
          (CSS field-sizing) instead of clipping on one line. Enter commits
          (blur); Shift+Enter inserts a newline. */}
      <textarea
        rows={1}
        aria-label={`${columnTitle} for ${rowKey}`}
        defaultValue={initial}
        onChange={(e) => {
          valRef.current = e.currentTarget.value;
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        onBlur={commit}
      />
    </td>
  );
}

// One reused offscreen canvas for content-width measurement. Measuring DOM text
// (not diagram geometry) — the no-DOM-measure rule is diagram-scoped, and this is
// a one-time width estimate, so a `measureText` here is deliberate and allowed.
let measureCanvas: HTMLCanvasElement | null = null;
function textWidth(s: string, font: string): number | null {
  try {
    measureCanvas ??= document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    if (!ctx) return null; // jsdom / no-canvas → caller falls back to fixed-equal
    ctx.font = font;
    return ctx.measureText(s).width;
  } catch {
    return null;
  }
}

/** Per-column pixel width from header + a bounded row sample; clamped [MIN,MAX].
 *  null when text can't be measured (jsdom) — the table then lays out fixed-equal. */
function measureColumnWidths(data: TableViewModel, font: string): number[] | null {
  const HEADER_EXTRA = 34; // room for the ⋯ menu + sort caret
  const PAD = 24;
  const MIN = 64;
  const MAX = 360;
  const SAMPLE = 120;
  const step = Math.max(1, Math.floor(data.rows.length / SAMPLE));
  const out: number[] = [];
  for (let ci = 0; ci < data.columns.length; ci++) {
    const header = textWidth(data.columns[ci]!.title, font);
    if (header == null) return null;
    let w = header + HEADER_EXTRA;
    for (let r = 0; r < data.rows.length; r += step) {
      const cell = data.rows[r]!.cells[ci];
      if (cell == null) continue;
      const cw = textWidth(String(cell), font);
      if (cw != null) w = Math.max(w, cw + PAD);
    }
    out.push(Math.min(MAX, Math.max(MIN, Math.ceil(w))));
  }
  return out;
}

/** Measure column widths once when windowing turns on (or the data changes). */
function useMeasuredColWidths(
  scrollRef: RefObject<HTMLDivElement | null>,
  data: TableViewModel,
  active: boolean,
): number[] | null {
  const [widths, setWidths] = useState<number[] | null>(null);
  useLayoutEffect(() => {
    if (!active) {
      setWidths(null);
      return;
    }
    const el = scrollRef.current?.querySelector("table");
    const font = (el && getComputedStyle(el).font) || "14px sans-serif";
    setWidths(measureColumnWidths(data, font));
  }, [active, data, scrollRef]);
  return active ? widths : null;
}

/** Drag frame for the header row — owns the sensors + reorder commit. */
function SortableTableFrame({ columns, count, children }: { columns: ColumnOps; count: number; children: ReactNode }): ReactNode {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = Array.from({ length: count }, (_, i) => `col-${i}`);
  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    columns.reorder(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

/** Local per-header UI state, hoisted here so one menu/picker serves the row. */
interface HeaderUi {
  readonly renaming: number | null;
  readonly pickerAnchor: number | null;
}

/** Build the per-column ⋯ menu — same actions the hand-rolled menu carried. */
function columnMenuItems(
  index: number,
  columns: ColumnOps,
  t: ReturnType<typeof useTranslation>["t"],
  setUi: (fn: (s: HeaderUi) => HeaderUi) => void,
): readonly ContextMenuItem[] {
  return [
    { label: t("colMenuRename"), onClick: () => setUi((s) => ({ ...s, renaming: index })) },
    ...(columns.columnGetters[index]
      ? [
          {
            label: index === columns.sortedColumnIndex ? t("colMenuClearSort") : t("colMenuSort"),
            onClick: () => columns.toggleSort(index),
          },
        ]
      : []),
    { label: t("colMenuAdd"), onClick: () => setUi((s) => ({ ...s, pickerAnchor: index })) },
    { label: t("colMenuRemove"), danger: true, separator: true, onClick: () => columns.remove(index) },
  ];
}

function TableHeader({ data, columns }: { data: TableViewModel; columns?: ColumnOps | null }): ReactNode {
  const { t } = useTranslation("boardEdit");
  const [ui, setUi] = useState<HeaderUi>({ renaming: null, pickerAnchor: null });
  const menu = useContextMenu();

  // Display-only header — no ops wired.
  if (!columns) {
    return (
      <thead>
        <tr>
          {data.columns.map((col, i) => (
            <th key={i} data-editable={col.editable} title={col.editable ? "editable" : "read-only"}>
              {col.title}
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  return (
    <thead>
      <tr>
        {data.columns.map((col, i) => (
          <SortableHeader
            key={`col-${i}`}
            id={`col-${i}`}
            index={i}
            title={col.title}
            editable={col.editable}
            sortable={columns.columnGetters[i] !== null}
            sorted={i === columns.sortedColumnIndex}
            renaming={ui.renaming === i}
            pickerOpen={ui.pickerAnchor === i}
            pickerOptions={columns.options}
            onSort={() => columns.toggleSort(i)}
            onMenu={(e) => menu.open(e, columnMenuItems(i, columns, t, setUi))}
            onRenameCommit={(title) => {
              setUi((s) => ({ ...s, renaming: null }));
              if (title !== col.title) columns.rename(i, title);
            }}
            onRenameCancel={() => setUi((s) => ({ ...s, renaming: null }))}
            onPick={(getter) => columns.add(getter)}
            onPickerClose={() => setUi((s) => ({ ...s, pickerAnchor: null }))}
          />
        ))}
      </tr>
      {menu.node}
    </thead>
  );
}

interface SortableHeaderProps {
  readonly id: string;
  readonly index: number;
  readonly title: string;
  readonly editable: boolean;
  readonly sortable: boolean;
  readonly sorted: boolean;
  readonly renaming: boolean;
  readonly pickerOpen: boolean;
  readonly pickerOptions: ColumnOps["options"];
  readonly onSort: () => void;
  readonly onMenu: (e: MouseEvent) => void;
  readonly onRenameCommit: (title: string) => void;
  readonly onRenameCancel: () => void;
  readonly onPick: (getter: TableGetterSpec) => void;
  readonly onPickerClose: () => void;
}

function SortableHeader(props: SortableHeaderProps): ReactNode {
  const { t } = useTranslation("boardEdit");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // `sticky` (not `relative`) so the interactive header pins at the top of a
    // TALL scroll like the display-only header does — and still anchors the
    // absolutely-positioned column picker (sticky is a positioning context too).
    position: "sticky" as const,
    top: 0,
    zIndex: isDragging ? 2 : 1,
    cursor: "grab",
    opacity: isDragging ? 0.6 : undefined,
  };
  return (
    <th
      ref={setNodeRef}
      style={style}
      data-editable={props.editable}
      data-sorted={props.sorted || undefined}
      aria-sort={props.sorted ? "ascending" : undefined}
      {...attributes}
      {...listeners}
    >
      {props.renaming ? (
        <input
          autoFocus
          aria-label={t("colRenameAria", { title: props.title })}
          defaultValue={props.title}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") props.onRenameCommit((e.target as HTMLInputElement).value);
            else if (e.key === "Escape") props.onRenameCancel();
          }}
          onBlur={(e) => props.onRenameCommit(e.target.value)}
        />
      ) : (
        <span className="flex items-center justify-between gap-1">
          {/* No `stopPropagation` on pointerdown: the drag sensor's 4px
              activation distance disambiguates a click (→ sort) from a drag
              (→ reorder), so the title doubles as a drag handle. */}
          <button
            type="button"
            className="cursor-pointer disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            style={{ background: "transparent", font: "inherit", color: "inherit", padding: 0 }}
            disabled={!props.sortable}
            title={props.sortable ? t("colSortTitle") : t("colNotSortable")}
            onClick={() => props.sortable && props.onSort()}
          >
            {props.title}
            {props.sorted && <span aria-hidden="true"> ▲</span>}
          </button>
          <IconButton
            size="xs"
            aria-label={t("colMenuAria", { title: props.title })}
            className="shrink-0 opacity-50 hover:opacity-100"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              props.onMenu(e);
            }}
          >
            <MoreVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </IconButton>
        </span>
      )}
      {props.pickerOpen && (
        <TableColumnPicker options={props.pickerOptions} onPick={props.onPick} onClose={props.onPickerClose} />
      )}
    </th>
  );
}
